// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useHandlerManageTags } from '@shared/tag-management/hooks/use-handler-manage-tags';
import { ManageTagsModal } from '@shared/tag-management/components/tags-manager';
import { tickerTagApi } from '@shared/data-access/api/engine/ticker-tag-api';
import {
  selectTagsByTicker,
  selectWatchlistPositions,
  tagManagerSlice,
} from '@shared/data-access/slices/tag-manager';
import { removeWatchListPosition } from '@shared/data-access/slices/tag-manager/tag-manager-thunk';
import {
  appStore as store,
  useAppDispatch,
  useAppSelector,
} from '@advisor-app/store/store';
import { TickerTagFeature } from '@shared/data-access/interfaces/api/engine/citec';
import type { Ticker } from '@shared/data-access/interfaces/redux/portfolio-state';
import {
  AppIcon,
  OptionsSelector,
  SectionContainer,
} from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { useTranslation } from 'react-i18next';
import { type FC, useCallback, useMemo, useState } from 'react';
import { WatchlistTable } from './watchlist-table';

export const Watchlist: FC = () => {
  const { t } = useTranslation('research');
  const dispatch = useAppDispatch();
  const [openTagsModal, setOpenTagsModal] = useState(false);

  const watchlist = useAppSelector(state => selectWatchlistPositions(state));

  const [deleteTickerTag] = tickerTagApi.useDeleteTickerTagMutation();

  const onRemoveItem = useCallback(
    (ticker: Ticker, tag: string) => {
      // TODO: FIX
      //  An input selector returned a different result when passed same arguments.
      //  This means your output selector will likely run more frequently than intended.
      //  Avoid returning a new reference inside your input selector, e.g.
      // `createSelector([state => state.todos.map(todo => todo.id)], todoIds => todoIds.length)`
      const watchlistTags = selectTagsByTicker(
        store.getState(),
        TickerTagFeature.SelectedAssets,
        [{ ticker, assetClass: '', name: '', isin: '', url_logo: '' }],
      );

      const [watchlistTagNames] = watchlistTags.map(t => t.tags);

      /* istanbul ignore next */
      if (tag) {
        deleteTickerTag({
          tickers: ticker,
          feature: TickerTagFeature.SelectedAssets,
          tags: tag,
        });
      }

      const isLastTag = !tag || watchlistTagNames.length === 1;
      if (isLastTag) {
        dispatch(tagManagerSlice.actions.removeWatchlistPosition(ticker));
        void dispatch(removeWatchListPosition({ ticker }));
      }
    },
    [deleteTickerTag, dispatch],
  );

  const handleOpenModal = useCallback(() => {
    setOpenTagsModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenTagsModal(false);
  }, []);

  const {
    onChangeTag,
    selectTag,
    tagNames,
    tags,
    positions,
    tickerTags,
    onCreateTag,
    onCreateTickerTag,
    suggestions,
    onAddTicker,
  } = useHandlerManageTags({
    feature: TickerTagFeature.SelectedAssets,
    dataSource: watchlist,
  });

  const defaultText = t('research.select-tag', { ns: 'research' });

  const optionsAvailable = useMemo(
    () => [defaultText, ...tagNames],
    [defaultText, tagNames],
  );

  return (
    <>
      <ManageTagsModal
        onClose={handleCloseModal}
        openModal={openTagsModal}
        tags={tags}
        tickerTags={tickerTags}
        onCreateTag={onCreateTag}
        onCreateTickerTag={onCreateTickerTag}
        suggestions={suggestions}
        assetsInput
        onAddTicker={onAddTicker}
        dataSource={watchlist}
      />
      <SectionContainer
        title={<Translation i18nKey="research.selected-assets" ns="research" />}
        popupName="my-research.selectedAssets"
        tooltip
        actions={
          <div className="flex items-center space-x-4">
            <div className="w-52">
              <OptionsSelector
                optionsAvailable={optionsAvailable}
                onChangeValueSelected={onChangeTag}
                optionSelected={selectTag}
                defaultText={defaultText}
                selectType="white"
                anchorOrigin={{
                  horizontal: 'center',
                  vertical: 'bottom',
                }}
              />
            </div>

            <button
              className="flex [&>*]:hover:text-primary"
              onClick={handleOpenModal}
              data-testid="manage-tags-test-id"
            >
              <h5 className="pr-2 text-primary-medium">
                <Translation i18nKey="research.manage-tags" ns="research" />
              </h5>
              <AppIcon icon="Lines" size="lg" color="disabled" />
            </button>
          </div>
        }
      >
        <WatchlistTable data={positions as any[]} onRemoveItem={onRemoveItem} />
      </SectionContainer>
    </>
  );
};
